import api from './api'
import { Oportunidade } from '@/types/oportunidade'
import {
  CuradoriaDecisaoRequest,
  CuradoriaFila,
  FiltroChecklistFila,
  ReservasResumo,
} from '@/types/curadoria'

const BASE_URL = '/api/v1/oportunidades'

/**
 * Curadoria + resumos de reserva (Onda 6 / A11). Service PRÓPRIO: o
 * `oportunidadeService` pertence a outra frente e não ganha métodos novos aqui.
 *
 * Escopo (sempre no backend, pelo token): a fila é OPERADOR+ (`TenantScope` —
 * OPERADOR/GERENTE/ADMIN são back-office global; o CONSULTOR é barrado no
 * `@PreAuthorize`); o resumo de reservas é CONSULTOR+ e recorta pela carteira do
 * consultor. Reserva fora do escopo na decisão volta como "Oportunidade não encontrada"
 * (fail-closed).
 */
export const curadoriaService = {
  /**
   * `GET /v1/oportunidades/curadoria` — KPIs + reservas com `statusCuradoria` derivado. P3:
   * `checklist = 'AGUARDANDO_VALIDACAO'` filtra NO SERVIDOR as linhas com item RECEBIDO esperando
   * o Operador (mesmo escopo da fila).
   */
  fila: async (checklist?: FiltroChecklistFila): Promise<CuradoriaFila> => {
    const url = checklist ? `${BASE_URL}/curadoria?checklist=${checklist}` : `${BASE_URL}/curadoria`
    return (await api.get<CuradoriaFila>(url)) as unknown as CuradoriaFila
  },

  /**
   * `POST /v1/oportunidades/{id}/curadoria/decisao` — APROVAR → APROVADA_ORIGINEI;
   * SOLICITAR_COMPLEMENTO → COM_PENDENCIA + pendência para o consultor; REPROVAR →
   * REPROVADA_ORIGINEI. Só transições existentes; o autor é o `sub` do token.
   */
  decidir: async (id: string, request: CuradoriaDecisaoRequest): Promise<Oportunidade> => {
    return (await api.post<Oportunidade>(
      `${BASE_URL}/${id}/curadoria/decisao`,
      request
    )) as unknown as Oportunidade
  },

  /**
   * `GET /v1/oportunidades/resumo` — KPIs de Minhas Reservas (ativas / a vencer / em
   * estruturação / ganhas) pela regra única do backend, no escopo de quem pede.
   */
  resumoReservas: async (): Promise<ReservasResumo> => {
    return (await api.get<ReservasResumo>(`${BASE_URL}/resumo`)) as unknown as ReservasResumo
  },

  /**
   * `POST /v1/oportunidades/{id}/defesa/enviar-revisao` — MINUTA (ou nulo, histórico
   * pré-V41) → EM_REVISAO. OPERADOR+ (quem redige). Endpoint do A08; o método vive aqui
   * porque a tela de aprovação (A11) é quem o dispara.
   */
  enviarDefesaParaRevisao: async (id: string): Promise<Oportunidade> => {
    return (await api.post<Oportunidade>(
      `${BASE_URL}/${id}/defesa/enviar-revisao`
    )) as unknown as Oportunidade
  },

  /**
   * `POST /v1/oportunidades/{id}/defesa/aprovar` — EM_REVISAO → APROVADA. ADMIN/GERENTE
   * (403 para os demais); o autor gravado é sempre o `sub` do token.
   */
  aprovarDefesa: async (id: string): Promise<Oportunidade> => {
    return (await api.post<Oportunidade>(`${BASE_URL}/${id}/defesa/aprovar`)) as unknown as Oportunidade
  },
}
